import { formatMinor, registrationStatus, type VendorSummary } from '@hvac/contracts';
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  cn,
  Field,
  Input,
  PageHeader,
  Pill,
} from '@hvac/ui';
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';

type SortOption = 'name' | 'spend' | 'records' | 'recent';
import { sessionQuery } from '../../shared/api/queries.js';
import { bookkeepingReferenceQuery } from '../../shared/api/bookkeeping-queries.js';
import {
  createVendor,
  setVendorTaxInfo,
  updateVendor,
  vendorKeys,
  vendorsQuery,
} from '../../shared/api/vendor-queries.js';

/**
 * Suppliers.
 *
 * The screen a bookkeeper lives in, and the one whose absence made everything
 * built since v0.3 unusable: the engines all revolve around a supplier and
 * there was no way to create one.
 *
 * The registration column is the point of the list view. It is what decides
 * whether an HST credit survives a CRA review, and until this release nothing
 * in the product could show it, let alone fix it.
 */

export function VendorsPage() {
  const { data: session } = useSuspenseQuery(sessionQuery());
  const organizationId = session.activeOrganizationId ?? '';
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [needsAttention, setNeedsAttention] = useState(false);
  const [sort, setSort] = useState<SortOption>('name');
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({
    displayName: '',
    legalName: '',
    gstHstNumber: '',
    defaultCategoryId: '',
    email: '',
    phone: '',
    website: '',
    notes: '',
  });
  const patch = (change: Partial<typeof draft>) =>
    setDraft((current) => ({ ...current, ...change }));
  const [error, setError] = useState<string | null>(null);

  const reference = useQuery(bookkeepingReferenceQuery(organizationId));
  const vendors = useQuery(
    vendorsQuery(organizationId, {
      ...(search.trim() === '' ? {} : { query: search.trim() }),
      ...(needsAttention ? { needsAttention: true } : {}),
      sort,
    }),
  );

  const create = useMutation({
    mutationFn: async () => {
      const trimmed = (value: string): string | undefined =>
        value.trim() === '' ? undefined : value.trim();
      const vendor = await createVendor(organizationId, {
        displayName: draft.displayName.trim(),
        ...(trimmed(draft.legalName) === undefined ? {} : { legalName: trimmed(draft.legalName) }),
        ...(draft.defaultCategoryId === '' ? {} : { defaultCategoryId: draft.defaultCategoryId }),
        ...(trimmed(draft.email) === undefined ? {} : { email: trimmed(draft.email) }),
        ...(trimmed(draft.phone) === undefined ? {} : { phone: trimmed(draft.phone) }),
        ...(trimmed(draft.website) === undefined ? {} : { website: trimmed(draft.website) }),
        ...(trimmed(draft.notes) === undefined ? {} : { notes: trimmed(draft.notes) }),
      });

      // The registration is its own record with its own effective date, so it
      // is set as a second, deliberate step rather than smuggled into create.
      const number = trimmed(draft.gstHstNumber);
      if (number !== undefined) {
        await setVendorTaxInfo(organizationId, vendor.id, {
          gstHstNumber: number,
          chargesGstHst: true,
          effectiveFrom: new Date().toISOString().slice(0, 10),
        });
      }
      return vendor;
    },
    onSuccess: () => {
      setDraft({
        displayName: '',
        legalName: '',
        gstHstNumber: '',
        defaultCategoryId: '',
        email: '',
        phone: '',
        website: '',
        notes: '',
      });
      setAdding(false);
      setError(null);
      void queryClient.invalidateQueries({ queryKey: vendorKeys.all(organizationId) });
    },
    onError: (caught) =>
      setError(caught instanceof Error ? caught.message : 'That supplier could not be added.'),
  });

  const items = vendors.data?.items ?? [];

  // Names that already exist, compared loosely: "wolseley" should stop you
  // before "Wolseley Canada" becomes two suppliers with half the history each.
  const normalise = (value: string): string => value.toLowerCase().replaceAll(/[^a-z0-9]/g, '');
  const typed = normalise(draft.displayName);
  const similar =
    typed.length < 3
      ? []
      : items.filter((entry) => {
          const existing = normalise(entry.displayName);
          return existing.includes(typed) || typed.includes(existing);
        });

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        eyebrow="Business"
        title="Suppliers"
        description="Who you buy from, what the system has learned about them, and whether their GST/HST registration will stand up."
      />

      {error !== null ? <Alert tone="danger">{error}</Alert> : null}

      {/* Adding a supplier by hand is the exception: they arrive on their own
          as receipts do. So the form stays folded until somebody asks for it,
          and when it opens it asks for everything worth knowing at once,
          entering a name and then hunting for the other eight fields is the
          hunt this product exists to remove. */}
      {adding ? (
        <Card>
          <CardHeader
            title="Add a supplier"
            description="Only the name is required. Everything else can wait, but it is quicker here than later."
            actions={
              <Button variant="ghost" size="sm" onClick={() => setAdding(false)}>
                Cancel
              </Button>
            }
          />
          <CardBody className="grid gap-4 sm:grid-cols-2">
            <Field label="Name" hint="What you call them.">
              {({ inputProps }) => (
                <Input
                  {...inputProps}
                  autoFocus
                  value={draft.displayName}
                  onChange={(event) => patch({ displayName: event.target.value })}
                  placeholder="Wolseley Canada"
                />
              )}
            </Field>

            <Field label="Legal name" hint="If it differs from the name on the invoice.">
              {({ inputProps }) => (
                <Input
                  {...inputProps}
                  value={draft.legalName}
                  onChange={(event) => patch({ legalName: event.target.value })}
                  placeholder="Wolseley Canada Inc."
                />
              )}
            </Field>

            <Field
              label="GST/HST number"
              hint="What a credit rests on. It is checked for shape, not invented."
            >
              {({ inputProps }) => (
                <Input
                  {...inputProps}
                  value={draft.gstHstNumber}
                  onChange={(event) => patch({ gstHstNumber: event.target.value })}
                  placeholder="123456789RT0001"
                />
              )}
            </Field>

            <Field label="Default account" hint="Where their purchases usually land.">
              {({ inputProps }) => (
                <select
                  {...inputProps}
                  value={draft.defaultCategoryId}
                  onChange={(event) => patch({ defaultCategoryId: event.target.value })}
                  className="h-12 w-full rounded-[var(--radius-control)] border border-border-strong bg-surface px-3 text-sm"
                >
                  <option value="">Not set</option>
                  {(reference.data?.categories ?? []).map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.code} · {category.name}
                    </option>
                  ))}
                </select>
              )}
            </Field>

            <Field label="Email">
              {({ inputProps }) => (
                <Input
                  {...inputProps}
                  type="email"
                  value={draft.email}
                  onChange={(event) => patch({ email: event.target.value })}
                  placeholder="ar@wolseley.ca"
                />
              )}
            </Field>

            <Field label="Phone">
              {({ inputProps }) => (
                <Input
                  {...inputProps}
                  value={draft.phone}
                  onChange={(event) => patch({ phone: event.target.value })}
                  placeholder="506-555-0100"
                />
              )}
            </Field>

            <Field label="Website" className="sm:col-span-2">
              {({ inputProps }) => (
                <Input
                  {...inputProps}
                  value={draft.website}
                  onChange={(event) => patch({ website: event.target.value })}
                  placeholder="wolseleyinc.ca"
                />
              )}
            </Field>

            <Field label="Notes" className="sm:col-span-2">
              {({ inputProps }) => (
                <Input
                  {...inputProps}
                  value={draft.notes}
                  onChange={(event) => patch({ notes: event.target.value })}
                  placeholder="Account 44812. Delivers Tuesdays."
                />
              )}
            </Field>

            <div className="flex items-center gap-3 sm:col-span-2">
              <Button
                onClick={() => create.mutate()}
                disabled={draft.displayName.trim().length < 2}
                isLoading={create.isPending}
              >
                Add supplier
              </Button>
              {draft.displayName.trim().length === 1 ? (
                <span className="text-xs text-content-muted">
                  A one-letter name will be hard to find later.
                </span>
              ) : null}
              {similar.length > 0 ? (
                <span className="text-xs text-warning">
                  Already here: {similar.map((entry) => entry.displayName).join(', ')}
                </span>
              ) : null}
            </div>
          </CardBody>
        </Card>
      ) : (
        <div>
          <Button variant="secondary" onClick={() => setAdding(true)}>
            Add a supplier
          </Button>
          <span className="ml-3 text-sm text-content-muted">
            Most suppliers arrive on their own, from the receipts you drop in.
          </span>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <input
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search suppliers"
          aria-label="Search suppliers"
          className="h-12 flex-1 rounded-[var(--radius-control)] border border-border-strong bg-surface px-4 text-[15px] transition-[border-color,box-shadow] duration-[var(--duration-quick)] focus:border-brand-400 focus:shadow-[var(--shadow-glow)] focus:outline-none"
        />
        <Button
          variant={needsAttention ? 'secondary' : 'ghost'}
          size="sm"
          onClick={() => setNeedsAttention(!needsAttention)}
        >
          Registration needs attention
        </Button>
      </div>

      <Card>
        <CardHeader title={`${items.length} supplier${items.length === 1 ? '' : 's'}`} />
        <CardBody className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="sticky top-0 z-10 bg-surface text-left text-xs uppercase tracking-wider text-content-subtle shadow-[inset_0_-1px_0_var(--color-border)]">
                  <SortableHeader label="Supplier" value="name" sort={sort} onSort={setSort} />
                  <th className="px-5 py-3 font-semibold">Default account</th>
                  <SortableHeader
                    label="Records"
                    value="records"
                    sort={sort}
                    onSort={setSort}
                    align="right"
                  />
                  <th className="px-5 py-3 text-right font-semibold">Typical</th>
                  <SortableHeader label="Last" value="recent" sort={sort} onSort={setSort} />
                  <th className="px-5 py-3 font-semibold">GST/HST</th>
                  <SortableHeader
                    label="Credits claimed"
                    value="spend"
                    sort={sort}
                    onSort={setSort}
                    align="right"
                  />
                </tr>
              </thead>
              <tbody>
                {items.map((vendor) => (
                  <VendorRow
                    key={vendor.id}
                    vendor={vendor}
                    organizationId={organizationId}
                    categories={reference.data?.categories ?? []}
                    onChanged={() => {
                      void queryClient.invalidateQueries({
                        queryKey: vendorKeys.all(organizationId),
                      });
                    }}
                    onError={(message) => setError(message)}
                  />
                ))}
              </tbody>
            </table>
          </div>

          {items.length === 0 ? (
            <p className="px-3 py-8 text-center text-sm text-content-muted">
              {needsAttention
                ? 'Every supplier that charges you HST has a registration number on file. That is the state you want.'
                : 'No suppliers yet. Add the first one above, or approve a document and the engine will ask you to.'}
            </p>
          ) : null}
        </CardBody>
      </Card>
    </div>
  );
}

function SortableHeader({
  label,
  value,
  sort,
  onSort,
  align,
}: {
  label: string;
  value: SortOption;
  sort: SortOption;
  onSort: (next: SortOption) => void;
  align?: 'right';
}) {
  const active = sort === value;
  return (
    <th className={cn('px-5 py-3 font-semibold', align === 'right' && 'text-right')}>
      <button
        type="button"
        onClick={() => onSort(value)}
        className={cn(
          'uppercase tracking-wide transition-colors hover:text-content',
          active && 'text-content',
        )}
      >
        {label}
        {active ? ' ↓' : ''}
      </button>
    </th>
  );
}

function VendorRow({
  vendor,
  organizationId,
  categories,
  onChanged,
  onError,
}: {
  vendor: VendorSummary;
  organizationId: string;
  categories: { id: string; name: string; code: string | null }[];
  onChanged: () => void;
  onError: (message: string) => void;
}) {
  const status = registrationStatus(vendor);

  // The most common single edit on this page, done where the eye already is.
  // A select rather than a click-to-edit cell: it says what it does before it
  // is touched, and it cannot be triggered by a stray click on the way to the
  // supplier's name.
  const setAccount = useMutation({
    mutationFn: (categoryId: string) =>
      updateVendor(organizationId, vendor.id, {
        defaultCategoryId: categoryId === '' ? null : categoryId,
      }),
    onSuccess: onChanged,
    onError: (caught) =>
      onError(caught instanceof Error ? caught.message : 'That account could not be set.'),
  });

  return (
    <tr className="border-b border-border transition-colors duration-[var(--duration-quick)] odd:bg-surface-muted/40 last:border-0 hover:bg-brand-50/60">
      <td className="px-5 py-3">
        <Link
          to="/suppliers/$vendorId"
          params={{ vendorId: vendor.id }}
          className="font-medium text-brand-600 hover:underline"
        >
          {vendor.displayName}
        </Link>
        {vendor.aliasCount > 1 ? (
          <span className="block text-xs text-content-subtle">
            {vendor.aliasCount} spellings resolve here
          </span>
        ) : null}
      </td>
      <td className="px-5 py-3">
        <select
          aria-label={`Default account for ${vendor.displayName}`}
          value={vendor.defaultCategoryId ?? ''}
          disabled={setAccount.isPending}
          onChange={(event) => setAccount.mutate(event.target.value)}
          className={cn(
            'w-full max-w-[16rem] rounded-[var(--radius-control)] border border-transparent',
            'bg-transparent px-2 py-1 text-sm transition-colors',
            'hover:border-border focus:border-border-strong focus:bg-surface',
            vendor.defaultCategoryId === null && 'text-content-subtle',
          )}
        >
          <option value="">Not set</option>
          {categories.map((category) => (
            <option key={category.id} value={category.id}>
              {category.code === null ? category.name : `${category.code} · ${category.name}`}
            </option>
          ))}
        </select>
      </td>
      <td className="tabular px-5 py-3 text-right">{vendor.recordCount}</td>
      <td className="tabular px-5 py-3 text-right">
        {vendor.meanAmountMinor === null ? '-' : formatMinor(vendor.meanAmountMinor)}
      </td>
      <td className="px-5 py-3 whitespace-nowrap">{vendor.lastRecordedOn ?? '-'}</td>
      <td className="px-5 py-3">
        <Pill
          tone={
            status.tone === 'danger' ? 'danger' : status.tone === 'warning' ? 'warning' : 'success'
          }
        >
          {status.label}
        </Pill>
      </td>
      <td className="tabular px-5 py-3 text-right">{formatMinor(vendor.itcClaimedMinor)}</td>
    </tr>
  );
}
